import { ArrowLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

/**
 * Public, auth-free video triage page. The HMF magic-link flow points tenants
 * here directly via SMS / email — they do NOT need to be logged into the
 * Trinium portal first. Renders the official Help me Fix widget iframe filling
 * the entire viewport so its multi-step UI (camera permissions, photo upload,
 * checkboxes) never gets cut off on mobile. Trinium chrome is reduced to a
 * small overlay back-button.
 */
const HMF_COMPANY_ID = 1382;

export function VideoTriage() {
  const [params] = useSearchParams();
  const deeplink = params.get("deeplink");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (deeplink && !/^[A-Za-z0-9_-]{8,64}$/.test(deeplink)) {
      setError("This triage link looks malformed. Request a new one from your repair report.");
    }
  }, [deeplink]);

  const base = `https://app.helpmefix.io/widgets/${HMF_COMPANY_ID}`;
  const src = deeplink ? `${base}?deeplink=${encodeURIComponent(deeplink)}` : `${base}?email=`;

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-4 bg-bg">
        <div className="max-w-sm w-full text-center space-y-4">
          <h1 className="text-xl font-semibold text-ink">Triage link issue</h1>
          <p className="text-sm text-ink-dim">{error}</p>
          <Link
            to="/repairs"
            className="inline-flex items-center gap-1 h-11 px-5 rounded-xl bg-accent text-accent-on font-medium"
          >
            Back to repairs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black">
      <iframe
        ref={iframeRef}
        id="hmf-iframe"
        title="Help me Fix video triage"
        src={src}
        allow="camera https://app.helpmefix.io; microphone https://app.helpmefix.io; fullscreen"
        className="w-full h-full block border-0"
      />
      <Link
        to="/repairs"
        className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur text-white text-sm font-medium hover:bg-black/70"
        aria-label="Close video triage"
      >
        <ArrowLeft size={14} />
        Back
      </Link>
    </div>
  );
}
